<script setup lang="ts">
import { AssociatedAccount, Trainer } from "../../types/PersologOnlineTypes.ts";
import { LanguageManagementAPI, ProfileAdministrationAPI } from "../../api";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import UserAvatar from "@persolog/ui/UserAvatar.vue";
import TransferDialog from "./TransferDialog.vue";
import StockDialog from "./StockDialog.vue";
import { i18n } from "../../main.ts";

const { t } = useI18n();

const props = defineProps({
    associatedAccounts: {
        type: Array as () => AssociatedAccount[],
        required: true,
    },
    trainer: {
        type: Object as () => Trainer,
        required: true,
    },
    direction: {
        type: String as () => "ACCESSED" | "ACCESSIBLE",
        required: true,
    },
});

const links = ref(props.associatedAccounts);
const showTransferDialog = ref(false);
const showStockDialog = ref(false);
const activeAccount = ref<Trainer>();
const texts = ref<Record<string, string>>({});

const rows = ref(5);
const showPaginator = computed(() => links.value.length > rows.value);

const emits = defineEmits(["update:links", "update:impersonated"]);

async function unlink(accountLink: AssociatedAccount) {
    if (accountLink.id) {
        await ProfileAdministrationAPI.unlinkAccount(accountLink.id);
        links.value = links.value.filter((link) => link.id !== accountLink.id);
        emits("update:links", links.value);
    }
}

function impersonate(account: AssociatedAccount) {
    const accountStr = JSON.stringify(account);
    localStorage.setItem("impersonatedAccount", accountStr);
    emits("update:impersonated", account);
}

function stock(account: AssociatedAccount) {
    showStockDialog.value = true;
    activeAccount.value = account.associatedAccount;
}

function transfer(account: AssociatedAccount) {
    showTransferDialog.value = true;
    activeAccount.value = account.associatedAccount;
}

onMounted(async () => {
    const result = await LanguageManagementAPI.fetchTexts("products");

    texts.value = Object.fromEntries(
        result.map((id) => [
            id.identifier,
            id.texts.find((t) => t.language === i18n.global.locale.value)?.text ??
            id.texts.find((t) => t.language === "en")?.text ??
            id.identifier
        ])
    );
});
</script>
<template>
    <DataTable
        :id="`account-link-table_data-table_${direction.toLowerCase()}`"
        :value="links"
        :paginator="showPaginator"
        :rows="rows"
        table-style="min-width: 78rem"
    >
        <template #header>
            <span v-if="direction === 'ACCESSED'" class="font-normal">
                {{ t("header.accountSettings.accountLinks.accessing.instructions") }}
            </span>
            <span v-else class="font-normal">
                {{ t("header.accountSettings.accountLinks.accessed.instructions") }}
            </span>
        </template>
        <Column :header="t('header.accountSettings.accountLinks.account')">
            <template #body="slotProps">
                <div v-if="direction === 'ACCESSED'" class="flex items-center gap-2">
                    <UserAvatar :image-url="slotProps.data.trainer.portrait" :trainer="slotProps.data.trainer" />
                    {{ slotProps.data.trainer.firstname }} {{ slotProps.data.trainer.lastname }}
                    <span :style="{ color: 'gray' }"> ({{ slotProps.data.trainer.email }}) </span>
                </div>
                <div v-else class="flex items-center gap-2">
                    <UserAvatar
                        :image-url="slotProps.data.associatedAccount.portrait"
                        :trainer="slotProps.data.associatedAccount"
                    />
                    {{ slotProps.data.associatedAccount.firstname }} {{ slotProps.data.associatedAccount.lastname }}
                    <span :style="{ color: 'gray' }"> ({{ slotProps.data.associatedAccount.email }}) </span>
                </div>
            </template>
        </Column>
        <Column field="rights" :header="t('header.accountSettings.accountLinks.rights')">
            <template #body="slotProps">
                {{ t(`header.accountSettings.accountLinks.${slotProps.data.rights.toLowerCase()}`) }}
            </template>
        </Column>
        <Column :header="t('header.accountSettings.accountLinks.actions')">
            <template #body="slotProps">
                <div v-if="direction === 'ACCESSED'" class="flex gap-2">
                    <Button
                        icon="pi pi-trash"
                        rounded
                        outlined
                        @click="unlink(slotProps.data)"
                        v-tooltip="t('header.accountSettings.accountLinks.tooltips.delete')"
                    ></Button>
                </div>
                <div v-else class="flex gap-2">
                    <Button
                        icon="pi pi-trash"
                        rounded
                        outlined
                        @click="unlink(slotProps.data)"
                        v-tooltip="t('header.accountSettings.accountLinks.tooltips.delete')"
                    ></Button>
                    <Button
                        v-if="slotProps.data.rights === 'PROJECTS'"
                        icon="pi pi-user"
                        rounded
                        outlined
                        @click="impersonate(slotProps.data)"
                        v-tooltip="t('header.accountSettings.accountLinks.tooltips.impersonate')"
                    ></Button>
                    <Button
                        v-if="slotProps.data.rights === 'PROJECTS'"
                        icon="pi pi-warehouse"
                        v-tooltip="t('header.accountSettings.purchases')"
                        rounded
                        outlined
                        @click="stock(slotProps.data)"
                    ></Button>
                    <Button
                        icon="pi pi-file-export"
                        rounded
                        outlined
                        @click="transfer(slotProps.data)"
                        v-tooltip="t('header.accountSettings.accountLinks.tooltips.transfer')"
                    ></Button>
                </div>
            </template>
        </Column>
    </DataTable>
    <TransferDialog
        v-if="activeAccount && showTransferDialog"
        :visible-toggle="showTransferDialog"
        :account="activeAccount"
        :trainer="trainer"
        @update:visible="showTransferDialog = $event"
    />
    <StockDialog
        v-if="activeAccount && showStockDialog"
        :visible-toggle="showStockDialog"
        :texts="texts"
        :account="activeAccount"
        @update:visible="(visible) => (showStockDialog = visible)"
    />
</template>
